import { Locker } from "shared/locker"
import { LockerThingTypeMrn } from "shared/lockerData"
import { appendNoteLine } from "./appendNoteLine"
import { isOutOfService } from "./lockerServiceState"

/**
 * Manual override of a locker's V1→V2 migration status (DEV-193).
 *
 * The SSTv2 migration pipeline drives three attributes on Locker Things, and
 * `Ap_GetLockers` hides a locker from user-facing reads when EITHER
 * `migrationDisabled` or `pendingActivation` is set:
 *
 *   before cutover : V2 `pendingActivation: true`  -> hidden, V1 serves
 *   after  cutover : V1 `migrationDisabled: true`  -> hidden, V2 serves
 *
 * When a migration stalls or half-lands, those flags can describe a cutover
 * that never really happened — a V1 locker marked superseded by a V2 locker
 * that is not actually usable, with no further partner event coming to fix it.
 * This module holds the rules an operator uses to assert the truth by hand; the
 * React panel is a thin shell over it. Pure on purpose: the flag combinations
 * are the risky part, so they are unit-tested without a DOM.
 */

export type LockerMigrationState = "active" | "superseded" | "pendingActivation"

export type LockerMigrationFlags = {
  migrationDisabled: boolean
  pendingActivation: boolean
  supersededByLockerThingId: string | null
}

/** Which side of a migration pair the OTHER locker sits on. */
export type LockerCounterpartRole = "v1" | "v2"

export type LockerCounterpart = {
  id: string
  role: LockerCounterpartRole
}

/** A single Thing patch, shaped for `updateThings({ body: { things } })`. */
export type LockerMigrationPatch = {
  id: string
  tenantId: string
  thingTypeMrn: string
  migrationDisabled?: boolean
  pendingActivation?: boolean
  supersededByLockerThingId?: string | null
  migrationNote: string
}

/** `migrationNote` is capped at 10 000 chars by `lockerAttrsUpdateSchema`. */
export const MIGRATION_NOTE_MAX = 10000

export const readMigrationFlags = (
  locker?: Partial<Locker> | null,
): LockerMigrationFlags => ({
  migrationDisabled: !!locker?.migrationDisabled,
  pendingActivation: !!locker?.pendingActivation,
  supersededByLockerThingId: locker?.supersededByLockerThingId ?? null,
})

/**
 * The state a human would name, given the flags. `migrationDisabled` wins the
 * tie because it is the post-cutover flag — a locker carrying both is the
 * incoherent case (see `hasIncoherentFlags`) and is reported as superseded so
 * the label never contradicts `isHiddenFromUsers`.
 */
export const deriveMigrationState = (
  flags: LockerMigrationFlags,
): LockerMigrationState => {
  if (flags.migrationDisabled) return "superseded"
  if (flags.pendingActivation) return "pendingActivation"
  return "active"
}

/** Mirrors the `Ap_GetLockers` filter exactly — absent attribute == visible. */
export const isHiddenFromUsers = (flags: LockerMigrationFlags): boolean =>
  flags.migrationDisabled || flags.pendingActivation

/**
 * Both hide-flags set at once. Only `migration:rollback:done` writes this
 * (quarantining a V2 locker), so on a V1 locker it always means a broken run.
 */
export const hasIncoherentFlags = (flags: LockerMigrationFlags): boolean =>
  flags.migrationDisabled && flags.pendingActivation

/**
 * The other locker in the migration pair, if this Thing still records the link.
 * A V1 locker points forward via `supersededByLockerThingId`; a V2 locker points
 * back via `migratedFromLockerThingId`.
 */
export const resolveCounterpart = (
  locker?: Partial<Locker> | null,
): LockerCounterpart | undefined => {
  if (locker?.supersededByLockerThingId) {
    return { id: locker.supersededByLockerThingId, role: "v2" }
  }
  if (locker?.migratedFromLockerThingId) {
    return { id: locker.migratedFromLockerThingId, role: "v1" }
  }
  return undefined
}

/**
 * Flags for the locker being overridden.
 *
 * `active` CLEARS `supersededByLockerThingId` rather than leaving it as
 * ancestry: `guardActionReqTarget` refuses any SstLocker* action-req aimed at a
 * locker that has a successor, so a "restored" locker that kept the pointer
 * would be visible to users and yet unable to execute a single door action.
 * Ancestry survives on `migratedFromSstV1LockerId` / `sstv2MigrationThingId` /
 * `migrationNote`, and the V2 side keeps its own `migratedFromLockerThingId`
 * back-pointer, so nothing is actually lost.
 */
export const flagsForState = (
  state: LockerMigrationState,
  current: LockerMigrationFlags,
): LockerMigrationFlags => {
  switch (state) {
    case "active":
      return {
        migrationDisabled: false,
        pendingActivation: false,
        supersededByLockerThingId: null,
      }
    case "superseded":
      return {
        migrationDisabled: true,
        pendingActivation: false,
        supersededByLockerThingId: current.supersededByLockerThingId,
      }
    case "pendingActivation":
      return {
        migrationDisabled: false,
        pendingActivation: true,
        supersededByLockerThingId: current.supersededByLockerThingId,
      }
  }
}

/**
 * Flags for the PAIRED locker, so exactly one unit stays user-visible.
 *
 * Hiding uses each side's native flag (V1 → `migrationDisabled` + successor
 * pointer, mirroring the cutover write; V2 → `pendingActivation`, mirroring how
 * it is born). Showing clears BOTH hide-flags, because a rollback-quarantined V2
 * carries `migrationDisabled` as well and clearing only `pendingActivation`
 * would leave it invisible.
 */
export const counterpartFlagsFor = (args: {
  targetState: LockerMigrationState
  role: LockerCounterpartRole
  primaryLockerId: string
}): Partial<LockerMigrationFlags> => {
  const { targetState, role, primaryLockerId } = args
  const primaryBecomesVisible = targetState === "active"

  if (!primaryBecomesVisible) {
    // This locker is being hidden, so the counterpart takes over.
    return { migrationDisabled: false, pendingActivation: false, ...(role === "v1" ? { supersededByLockerThingId: null } : {}) }
  }

  return role === "v1"
    ? { migrationDisabled: true, supersededByLockerThingId: primaryLockerId }
    : { pendingActivation: true }
}

/** One column of the before→after pair diagram. */
export type LockerPairSide = {
  id: string
  before: LockerMigrationState
  after: LockerMigrationState
  beforeVisible: boolean
  afterVisible: boolean
  changed: boolean
  /**
   * The operator service switch is a THIRD hide-flag this panel does not write.
   * It is carried here so the diagram's visibility claim stays true: without it,
   * a locker an operator took out of service would be drawn "visible to users"
   * the moment the migration flags say active, and the pair verdict would read
   * `ok` while users can in fact see neither unit.
   */
  outOfService: boolean
}

export type LockerPairVerdict =
  | "ok" // exactly one of the pair is user-visible
  | "single" // this locker has no pair recorded
  | "bothVisible" // users would be offered the same physical unit twice
  | "bothHidden" // users would lose the unit entirely
  | "unknownCounterpart" // pair recorded but its Thing has not loaded yet

export type LockerPairPreview = {
  primary: LockerPairSide
  counterpart?: LockerPairSide
  verdict: LockerPairVerdict
}

const sideFrom = (
  id: string,
  beforeFlags: LockerMigrationFlags,
  afterFlags: LockerMigrationFlags,
  outOfService: boolean,
): LockerPairSide => {
  const before = deriveMigrationState(beforeFlags)
  const after = deriveMigrationState(afterFlags)
  // `outOfService` gates both columns because this panel never changes it: it is
  // a constant across the before→after of a migration-flag write.
  return {
    id,
    before,
    after,
    beforeVisible: !isHiddenFromUsers(beforeFlags) && !outOfService,
    afterVisible: !isHiddenFromUsers(afterFlags) && !outOfService,
    changed: before !== after,
    outOfService,
  }
}

/**
 * What the user-facing read path will do once the pending override lands, for
 * both halves of the pair. Drives the diagram and the go/no-go verdict — the
 * operator should never have to hold "which flag hides which side" in their
 * head to know whether they are about to double-list or orphan a locker.
 */
export const previewPair = (args: {
  locker: Locker
  targetState: LockerMigrationState
  counterpart?: LockerCounterpart
  counterpartLocker?: Partial<Locker> | null
  applyToCounterpart: boolean
}): LockerPairPreview => {
  const { locker, targetState, counterpart, counterpartLocker, applyToCounterpart } = args

  const primaryBefore = readMigrationFlags(locker)
  const primary = sideFrom(
    locker.id,
    primaryBefore,
    flagsForState(targetState, primaryBefore),
    isOutOfService(locker),
  )

  if (!counterpart) return { primary, verdict: "single" }
  if (!counterpartLocker) {
    return { primary, verdict: "unknownCounterpart" }
  }

  const counterpartBefore = readMigrationFlags(counterpartLocker)
  const counterpartAfter = applyToCounterpart
    ? {
        ...counterpartBefore,
        ...counterpartFlagsFor({
          targetState,
          role: counterpart.role,
          primaryLockerId: locker.id,
        }),
      }
    : counterpartBefore

  const side = sideFrom(
    counterpart.id,
    counterpartBefore,
    counterpartAfter,
    isOutOfService(counterpartLocker),
  )
  const visibleCount = [primary.afterVisible, side.afterVisible].filter(Boolean).length

  return {
    primary,
    counterpart: side,
    verdict: visibleCount === 1 ? "ok" : visibleCount === 2 ? "bothVisible" : "bothHidden",
  }
}

const STATE_LABELS: Record<LockerMigrationState, string> = {
  active: "ACTIVE",
  superseded: "SUPERSEDED",
  pendingActivation: "PENDING_ACTIVATION",
}

/** Newest entry first, so the head of the note is always the current truth. */
export const appendOverrideNote = (
  existing: string | undefined,
  line: string,
): string => appendNoteLine(existing, line, MIGRATION_NOTE_MAX)

const overrideNoteLine = (args: {
  state: LockerMigrationState
  reason: string
  now: string
  actor?: string
  counterpartOf?: string
}): string => {
  const by = args.actor ? ` by ${args.actor}` : ""
  const of = args.counterpartOf ? ` (paired with ${args.counterpartOf})` : ""
  return `[manual override ${args.now}${by}] ${STATE_LABELS[args.state]}${of} — ${args.reason}`
}

export type BuildOverridePatchesArgs = {
  locker: Locker
  targetState: LockerMigrationState
  /** Advanced-section value; `undefined` keeps whatever `flagsForState` decides. */
  supersededByLockerThingIdOverride?: string | null
  reason: string
  counterpart?: LockerCounterpart
  /** Ignored unless `counterpart` is set. */
  applyToCounterpart?: boolean
  /** Only used to carry the counterpart's existing note forward. */
  counterpartLocker?: Partial<Locker> | null
  actor?: string
  /** Injected so the output is deterministic in tests. */
  now: string
}

export const buildOverridePatches = (
  args: BuildOverridePatchesArgs,
): LockerMigrationPatch[] => {
  const {
    locker,
    targetState,
    supersededByLockerThingIdOverride,
    reason,
    counterpart,
    applyToCounterpart,
    counterpartLocker,
    actor,
    now,
  } = args

  const next = flagsForState(targetState, readMigrationFlags(locker))
  const supersededByLockerThingId =
    supersededByLockerThingIdOverride === undefined
      ? next.supersededByLockerThingId
      : supersededByLockerThingIdOverride || null

  const patches: LockerMigrationPatch[] = [
    {
      id: locker.id,
      tenantId: locker.tenantId,
      thingTypeMrn: LockerThingTypeMrn,
      migrationDisabled: next.migrationDisabled,
      pendingActivation: next.pendingActivation,
      supersededByLockerThingId,
      migrationNote: appendOverrideNote(
        locker.migrationNote,
        overrideNoteLine({ state: targetState, reason, now, actor }),
      ),
    },
  ]

  if (counterpart && applyToCounterpart) {
    const counterpartFlags = counterpartFlagsFor({
      targetState,
      role: counterpart.role,
      primaryLockerId: locker.id,
    })
    const counterpartState = deriveMigrationState(
      readMigrationFlags(counterpartFlags as Partial<Locker>),
    )
    patches.push({
      id: counterpart.id,
      tenantId: locker.tenantId,
      thingTypeMrn: LockerThingTypeMrn,
      ...counterpartFlags,
      migrationNote: appendOverrideNote(
        counterpartLocker?.migrationNote,
        overrideNoteLine({
          state: counterpartState,
          reason,
          now,
          actor,
          counterpartOf: locker.id,
        }),
      ),
    })
  }

  return patches
}

/**
 * Field-level diff for the confirmation dialog. Raw attribute names on purpose —
 * this is a super-admin escape hatch, and paraphrasing the write is how an
 * operator ends up approving something other than what lands in DynamoDB.
 */
export const summarizePatch = (
  patch: LockerMigrationPatch,
  before: LockerMigrationFlags,
): string[] => {
  const rows: string[] = []
  const fmt = (v: unknown) => (v === null ? "null" : String(v))
  if (patch.migrationDisabled !== undefined && patch.migrationDisabled !== before.migrationDisabled) {
    rows.push(`migrationDisabled: ${fmt(before.migrationDisabled)} → ${fmt(patch.migrationDisabled)}`)
  }
  if (patch.pendingActivation !== undefined && patch.pendingActivation !== before.pendingActivation) {
    rows.push(`pendingActivation: ${fmt(before.pendingActivation)} → ${fmt(patch.pendingActivation)}`)
  }
  if (
    patch.supersededByLockerThingId !== undefined &&
    patch.supersededByLockerThingId !== before.supersededByLockerThingId
  ) {
    rows.push(
      `supersededByLockerThingId: ${fmt(before.supersededByLockerThingId)} → ${fmt(patch.supersededByLockerThingId)}`,
    )
  }
  return rows
}
